import {Popover} from '@ezcater/tapas';
import {twMerge} from 'tailwind-merge';

type MenuPopoverSide = React.ComponentPropsWithoutRef<typeof Popover.Content>['side'];
type MenuPopoverAlign = React.ComponentPropsWithoutRef<typeof Popover.Content>['align'];
type MenuPopoverPlacement =
  NonNullable<MenuPopoverSide> | `${NonNullable<MenuPopoverSide>}-${NonNullable<MenuPopoverAlign>}`;
type PopoverRootProps = React.ComponentPropsWithoutRef<typeof Popover.Root>;

// Open instantly on hover by default (Base UI's default is 300ms),
// and keep the old 100ms grace period on leave so the pointer can
// travel from the trigger to the popup without it closing.
const HOVER_OPEN_DELAY_MS = 0;
const HOVER_CLOSE_DELAY_MS = 100;

type MenuPopoverProps = {
  ariaLabel?: string;
  arrowClasses?: string;
  // Rendered as the popover trigger via Base UI's `render` prop, so it must be a single element.
  children: React.ReactElement;
  className?: string;
  content: React.ReactNode;
  contentClasses?: string;
  isOpen?: PopoverRootProps['open'];
  onOpenChange?: PopoverRootProps['onOpenChange'];
  // Hover-open delay in ms.
  // Orders matches it to its exposure dwell.
  openDelay?: number;
  placement?: MenuPopoverPlacement;
};

const MenuPopover: React.FC<MenuPopoverProps> = ({
  ariaLabel = 'menu dropdown',
  arrowClasses,
  children,
  className,
  content,
  contentClasses,
  isOpen,
  onOpenChange,
  openDelay = HOVER_OPEN_DELAY_MS,
  placement = 'bottom-end',
}) => {
  const [side, align] = placement.split('-') as [
    NonNullable<MenuPopoverSide>,
    MenuPopoverAlign | undefined,
  ];

  return (
    // `openOnHover` on the trigger makes Base UI the single owner of the open state. Tracking
    // hover separately and OR-ing it with `isOpen` meant a click could toggle the popover shut the
    // instant hover had opened it, so clicking the trigger appeared to do nothing.
    <Popover.Root onOpenChange={onOpenChange} open={isOpen}>
      <Popover.Trigger
        closeDelay={HOVER_CLOSE_DELAY_MS}
        delay={openDelay}
        openOnHover
        render={children}
      />
      <Popover.Content
        align={align}
        aria-label={ariaLabel}
        className={twMerge(
          'relative z-1300 mt-2 rounded-xl border bg-white p-6 shadow-lg outline-none',
          className,
        )}
        initialFocus={false}
        side={side}
      >
        <div
          aria-hidden="true"
          className={twMerge(
            'absolute -top-2 right-6 h-4 w-4 -translate-x-1/2 rotate-45 transform border-l border-t bg-white',
            arrowClasses,
          )}
        />
        <div
          className={twMerge(
            '-mr-6 max-h-[calc(100vh-140px)] overflow-auto pl-1 pr-7',
            contentClasses,
          )}
        >
          {content}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
};

export default MenuPopover;
